@value mobile, tablet, laptop from "@shared/styles/breakpoints.module.css";

@keyframes pulse {
  from {
    transform: scale(1);
    opacity: 0.55;
  }
  to {
    transform: scale(3);
    opacity: 0;
  }
}

.root {
  padding: clamp(var(--space-64), 7vw, var(--space-96)) var(--space-page) 0;
  background: var(--color-bg);
}

.header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-6) var(--space-20);
  margin-bottom: clamp(var(--space-20), 2.5vw, var(--space-32));
}

.title {
  font-family: var(--font-heading);
  font-size: var(--type-section);
  font-weight: 600;
  letter-spacing: normal;
  line-height: 1.1;
  text-transform: none;
  color: var(--color-text);
}

.layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) clamp(280px, 25vw, 360px);
  gap: clamp(var(--space-20), 2.5vw, var(--space-40));
  align-items: stretch;

  @media (max-width: laptop) {
    grid-template-columns: 1fr;
  }
}

.mapFrame {
  position: relative;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--space-12);
  padding: clamp(var(--space-12), 1.5vw, var(--space-24));
  background-color: var(--color-card);
  background-image:
    linear-gradient(color-mix(in srgb, var(--color-border) 35%, transparent) 1px, transparent 1px),
    linear-gradient(
      90deg,
      color-mix(in srgb, var(--color-border) 35%, transparent) 1px,
      transparent 1px
    );
  background-size: 40px 40px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-small);
}

.canvas {
  position: relative;
  container-type: inline-size;
}

.plot {
  position: relative;
}

.contours {
  display: block;
  width: 100%;
  height: auto;
}

.overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.mapStats {
  position: absolute;
  left: 1.5%;
  top: 0.5%;
  width: 82.4%;
  margin: 0;
  /* Крайние числа прижаты к краям полосы, промежутки равные — ряд занимает всю свободную ширину */
  display: flex;
  justify-content: space-between;
  gap: 2cqw;

  @media (max-width: tablet) {
    position: static;
    width: auto;
    margin-top: var(--space-16);
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--space-16);
  }
}

.mapStat {
  display: grid;
  min-width: 0;
}

.mapStatValue {
  order: -1;
  margin: 0;
  font-family: var(--font-heading);
  font-size: clamp(1.75rem, 4.2cqw, 2.75rem);
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: var(--color-text);
}

.mapStatLabel {
  font-size: clamp(0.8125rem, 1.4cqw, 0.9375rem);
  line-height: 1.25;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

.region {
  fill: var(--color-bg);
  stroke: var(--color-border);
  stroke-width: 0.6;
  vector-effect: non-scaling-stroke;
  transition: fill var(--motion-fast) ease;

  /* Регион с поставками — вторичный текст поверх подложки: в светлой теме темнее суши,
     в тёмной светлее, контраст с остальными регионами одинаковый */

  /* Подсветка — ответ на действие пользователя в списке регионов, поэтому акцентом */
}

.pulse {
  fill: none;
  stroke: var(--color-text);
  stroke-width: 1;
  vector-effect: non-scaling-stroke;
  pointer-events: none;
  transform-box: fill-box;
  transform-origin: center;
  animation: pulse var(--motion-pulse-duration) var(--motion-ease-out) infinite;
}

.dot {
  fill: var(--color-text);
  stroke: var(--color-card);
  stroke-width: 1;
  vector-effect: non-scaling-stroke;
  pointer-events: none;
  transition:
    fill var(--motion-fast) ease,
    stroke var(--motion-fast) ease;
}

.hitArea {
  fill: transparent;
  cursor: pointer;
}

.factoryGroup {
  pointer-events: none;
}

.factory {
  fill: none;
  stroke: var(--color-text);
}

.cityLabel {
  opacity: 1;
  transition: opacity var(--motion-fast) ease;

  fill: var(--color-text-muted);
  font-family: var(--font-body);
  paint-order: stroke;
  stroke: var(--color-card);

  /* Уже 560px карта слишком мелкая для подписей городов — остаётся только Иваново.
     Ширина холста (Canvas) равна ширине карты */
  @container (max-width: 559.98px) {
    display: none;
  }
}

.factoryLabel {
  opacity: 1;
  transition: opacity var(--motion-fast) ease;

  fill: var(--color-text);
  font-family: var(--font-body);
  font-weight: 700;
  letter-spacing: 0.5px;
  paint-order: stroke;
  stroke: var(--color-card);
}

.tooltip {
  position: absolute;
  z-index: 2;
  transform: translate(-50%, calc(-100% - var(--space-10)));
  width: max-content;
  max-width: min(300px, 80vw);
  padding: var(--space-10) var(--space-14);
  background: var(--color-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-small);
  box-shadow: var(--shadow-overlay);
}

.tooltipTitle {
  font-family: var(--font-heading);
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--color-text);
}

.tooltipMeta {
  font-size: var(--type-caption);
  color: var(--color-text-muted);
}

.tooltipList {
  margin: var(--space-6) 0 0;
  padding: 0;
  list-style: none;
  font-size: var(--type-caption);
  line-height: 1.4;
  color: var(--color-text);
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-8) var(--space-20);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--type-caption);
  color: var(--color-text-muted);

  li {
    display: inline-flex;
    align-items: center;
    gap: var(--space-8);
  }
}

.legendDot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--color-text);
  border: 1.5px solid var(--color-text);
}

.legendFactory {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid var(--color-text);
}

.aside {
  position: relative;
  min-width: 0;
  min-height: 420px;

  @media (max-width: laptop) {
    min-height: 0;
  }
}

.asideInner {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;

  @media (max-width: laptop) {
    position: static;
  }
}

.regionList {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin: 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid var(--color-border);
  overscroll-behavior: contain;

  /* на узком экране список не прокручивается внутри — он свёрнут, полный по кнопке */
  @media (max-width: laptop) {
    overflow: visible;
  }
}

.regionRow {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: baseline;
  gap: var(--space-2) var(--space-12);
  width: 100%;
  padding: var(--space-12) var(--space-10);
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--color-border);
  /* выбранная строка — inset-полоска акцентом, как в каталоге: содержимое не сдвигается */
  box-shadow: none;
  color: var(--color-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    background-color var(--motion-fast) ease,
    box-shadow var(--motion-fast) ease;
}

.regionDetails {
  display: grid;
  gap: var(--space-8);
  margin: 0;
  padding: var(--space-10) var(--space-10) var(--space-12) var(--space-20);
  list-style: none;
  background: var(--color-card);
  border-bottom: 1px solid var(--color-border);
  font-size: 0.875rem;
  line-height: 1.4;

  span {
    display: block;
    font-size: var(--type-caption);
    color: var(--color-text-muted);
  }
}

.regionName {
  min-width: 0;
  font-size: 1rem;
  line-height: 1.25;
}

.regionCount {
  font-family: var(--font-heading);
  font-size: 1.375rem;
  font-weight: 700;
  line-height: 1.1;
  color: var(--color-text);
}

.regionMeta {
  grid-column: 1 / -1;
  font-size: var(--type-caption);
  color: var(--color-text-muted);
}

.showAll {
  display: none;
  min-height: var(--layout-control-height);
  margin-top: var(--space-12);
  padding: var(--space-10) var(--space-20);
  background: transparent;
  border: 1px solid var(--color-primary);
  border-radius: var(--radius-small);
  color: var(--color-primary);
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: normal;
  text-transform: none;
  cursor: pointer;

  @media (max-width: laptop) {
    display: inline-flex;
    align-items: center;
    width: fit-content;
  }
}

.region[data-active="true"] {
  fill: var(--color-text-muted);
  fill-opacity: 0.22;
}
.region[data-highlight="true"] {
  fill: var(--color-primary);
  fill-opacity: 0.24;
}
.dot[data-progress="true"] {
  fill: var(--color-card);
  stroke: var(--color-text);
  stroke-width: 1.5;
}
.dot[data-active="true"] {
  fill: var(--color-primary);
  stroke: var(--color-card);
}
.dot[data-progress="true"][data-active="true"] {
  fill: var(--color-card);
  stroke: var(--color-primary);
}
.cityLabel[data-hidden="true"],
.factoryLabel[data-hidden="true"] {
  opacity: 0;
}
.tooltip[data-below="true"] {
  transform: translate(-50%, var(--space-10));
}
.legendDot[data-progress="true"] {
  background: transparent;
}
@media (max-width: laptop) {
  .regionItem[data-collapsed="true"] {
    display: none;
  }
}
.regionRow[data-active="true"] {
  background: var(--color-card);
  box-shadow: inset var(--space-3) 0 0 var(--color-primary);
}
